import { useState, useEffect, useRef, ChangeEventHandler } from 'react';
import { Backdrop, Box, Button, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import { GridColDef, GridAlignment } from '@mui/x-data-grid';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { MAX_PAGE_LIMIT } from '../../../constants/numbers';
import IResponse, { IPagination } from '../../../interfaces/response';
import BaranDataGrid from '../../common/BaranDataGrid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import IRole from '../../../interfaces/role';
import BasicModal from '../../common/BasicModal';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { LoadingButton } from '@mui/lab';
import IosSwitch from '../../common/IosSwitch';
import { exportToExcel } from '../../../utils/file';
import InformationCard from '../../common/InformationCard';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ForkLeftIcon from '@mui/icons-material/ForkLeft';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

interface IStats {
	total?: number;
	active?: number;
	inactive?: number;
	marketers?: number;
}

export default function RoleList() {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [fetchedData, setFetchedData] = useState<IRole[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(true);
	const [submitRoleLoading, setSubmitRoleLoading] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [stats, setStats] = useState<IStats>({});
	const [searchTerm, setSearchTerm] = useState('');
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [permissions, setPermissions] = useState<{ [key: string]: string }>();
	const [selectedRole, setSelectedRole] = useState<IRole>();
	const [refresh, setRefresh] = useState(1);
	const permissionsRef = useRef<{ [key: string]: HTMLInputElement | null }>({});
	const roleLableRef = useRef<HTMLInputElement | null>();

	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();

	const handleCreateNewPage = () => {
		setShowModal(true);
	};

	const handleEditRole = (role: IRole) => () => {
		setShowModal(() => {
			setSelectedRole(role);
			return true;
		});
	};
	const handleExportExcel = () => {
		exportToExcel({ data: fetchedData, fileName: 'payment_list', t });
	};

	const handleShowDeleteDialog = (role: IRole) => async () => {
		setShowDeleteModal(() => {
			setSelectedRole(role);
			return true;
		});
	};

	const handleDeleteRole = () => {
		setSubmitRoleLoading(true);
		axios<IResponse<any>>({
			url: `/admin/roles/${selectedRole!.id}`,
			method: 'DELETE',
		})
			.then(res => {
				if (res.data.success) {
					setShowDeleteModal(false);
					setRefresh(prev => prev + 1);
				}
			})
			.finally(() => {
				setSubmitRoleLoading(false);
			});
	};

	const columns: GridColDef<IRole>[] = [
		{
			field: 'label',
			headerName: t('title'),
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
			flex: 2,
		},
		{
			field: 'created_at',
			headerName: t('creation_date'),
			flex: 1,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 200,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return (
					<>
						<IconButton onClick={handleEditRole(params.row)}>
							<EditIcon className="text-text-secondary" />
						</IconButton>
						<IconButton onClick={handleShowDeleteDialog(params.row)}>
							<DeleteOutlineIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	useEffect(() => {
		axios<IResponse<any>>({
			url: '/admin/permissions/',
		}).then(res => {
			if (res.data.success) {
				setPermissions(res.data.data);
			}
		});
		axios<IResponse<IStats>>({
			url: '/admin/admins/stats',
		}).then(res => {
			if (res.data.success) {
				setStats(res.data.data);
			}
		});
	}, []);

	useEffect(() => {
		axios<IResponse<any>>({
			url: '/admin/roles/',
			params: {
				search: searchTerm,
			},
		}).then(res => {
			if (res.data.success) {
				setFetchedData(res.data.data);
			}
		});
	}, [searchTerm]);

	useEffect(() => {
		if (selectedRole && !selectedRole.permissions && selectedRole.id) {
			axios<IResponse<IRole>>({
				url: `/admin/roles/${selectedRole.id}`,
			}).then(res => {
				if (res.data.success) {
					setSelectedRole(res.data.data);
				}
			});
		}
	}, [selectedRole]);

	useEffect(() => {
		setLoading(true);
		axios<IResponse<IRole[]>>({
			url: `/admin/roles/`,
			params: {
				page,
				limit: pageSize,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [page, pageSize, refresh]);

	const handleCloseModal = () => {
		setSelectedRole(undefined);
		setShowModal(false);
	};

	const handleSubmitRole = () => {
		setSubmitRoleLoading(true);
		axios<IResponse<any>>({
			url: '/admin/roles/' + (selectedRole?.id ?? ''),
			method: selectedRole?.id ? 'PUT' : 'POST',
			data: {
				id: selectedRole?.id,
				title: roleLableRef.current?.value,
				permissions: Object.keys(permissionsRef.current)
					.map(key => {
						if (permissionsRef.current[key] && permissionsRef.current[key].checked) {
							return key;
						}
					})
					.filter(key => !!key),
			},
		})
			.then(res => {
				if (res.data.success) {
					setShowModal(false);
					setRefresh(prev => prev + 1);
					setPage(1);
				}
			})
			.finally(() => {
				setSubmitRoleLoading(false);
			});
	};

	const handleCloseDeleteDialog = () => {
		setSelectedRole(undefined);
		setShowDeleteModal(false);
	};

	const handleChangeSearch: ChangeEventHandler<HTMLInputElement> = e => {
		setSearchTerm(e.target.value);
	};

	return (
		<div className={'w-full'}>
			<Grid container rowSpacing={2} className="mb-5" columnSpacing={3}>
				<Grid xs={6} md={3}>
					<InformationCard title={t('all_users')} icon={<AccessTimeIcon />} color="green" description={stats.total?.toLocaleString()} />
				</Grid>
				<Grid xs={6} md={3}>
					<InformationCard title={t('active_users')} icon={<ForkLeftIcon />} color="secondary" description={stats.active?.toLocaleString()} />
				</Grid>
				<Grid xs={6} md={3}>
					<InformationCard title={t('inactive_users')} icon={<WarningAmberIcon />} color="orange" description={stats.inactive?.toLocaleString()} />
				</Grid>
				<Grid xs={6} md={3}>
					<InformationCard title={t('marketer_users')} icon={<LocalShippingIcon />} color="primary" description={stats.marketers?.toLocaleString()} />
				</Grid>
			</Grid>

			<BaranDataGrid<IRole> loading={loading} pagination={pagination} searchTerm={searchTerm} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page > 0 ? page : 1} setPage={setPage} onClick={handleCreateNewPage} actionButtonLable={t('add_new_role')} onClick2={fetchedData.length ? handleExportExcel : undefined} actionButtonLable2={t('export_excel')} handleSearch={handleChangeSearch} />

			<BasicModal
				open={showModal}
				onClose={handleCloseModal}
				title={t('add_new_role')}
				showBackIcon={false}
				action={
					<>
						<LoadingButton loading={submitRoleLoading} variant={'contained'} onClick={handleSubmitRole}>
							{t('submit')}
						</LoadingButton>
						<Button onClick={handleCloseModal} variant={'contained'} className={'bg-grey-300 px-5 text-text-secondary'}>
							{t('cancel')}
						</Button>
					</>
				}
				titleClassName={'text-center grow '}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-[736] rounded-lg'}
			>
				<Typography className={'mt-12 text-sm text-text-primary/90'}>{t('title')}</Typography>
				<TextField size={'small'} fullWidth defaultValue={selectedRole?.label} inputRef={roleLableRef} />
				<Box className={'relative mb-10 mt-8 flex flex-col justify-start'}>
					<Typography className={'mb-5 text-start text-base text-text-primary/90 sm:text-center'}>دسترسی</Typography>
					{selectedRole !== undefined && !selectedRole.permissions && selectedRole.id !== undefined ? (
						<Backdrop className="absolute bottom-0 left-0 right-0 top-0" open={true}>
							<CircularProgress />
						</Backdrop>
					) : (
						<Grid container className="px-1">
							{Object.entries(permissions ?? {}).map(([key, value]) => {
								return (
									<Grid xs={4} md={3} key={key} className={'flex flex-row justify-between gap-x-2'}>
										<IosSwitch name={key} label={value} defaultChecked={selectedRole?.permissions?.includes(key) ?? false} inputRef={(el: HTMLInputElement) => (permissionsRef.current[key] = el)} />
									</Grid>
								);
							})}
						</Grid>
					)}
				</Box>
			</BasicModal>

			<BasicModal
				open={showDeleteModal}
				onClose={handleCloseDeleteDialog}
				title={t('delete_role')}
				titleClassName={'text-center grow '}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-sm rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={handleCloseDeleteDialog} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton loading={submitRoleLoading} onClick={handleDeleteRole} variant="outlined" color="error">
							{t('delete')}
						</LoadingButton>
					</>
				}
			>
				<Typography>{t('delete_role_description', { role: selectedRole?.label })}</Typography>
			</BasicModal>
		</div>
	);
}
